/**
 *
 * @param { {organizations: Array<{id: string, name: string, url: string, level: string, parent: string, settings_inherit: Any, settings_local: Any}>} } state
 * @param getters
 * @returns Array<{id: string, name: string, level: string, parent: string, settings_inherit: Any, settings_local: Any}>
 */
import { haversineDistance } from "@/jscommon/src/lib/geo-tools"
import { merge as deepMerge } from "lodash"

export function relevantOrganizations (state, getters) {
  if (!state.organizations) {
    return []
  }
  const orgSettingsByUrl = getters.organizationSettingsByUrl
  return state.organizations.filter((value) => {
    return (
      (orgSettingsByUrl[value.url] ?? {})?.override?.enableWA ??
      value.level === "parish"
    )
  })
}

export function functionRelevantOrganizationsWithDistance (state, getters) {
  if (!state.coords) {
    return getters.relevantOrganizations
  } else {
    return getters.relevantOrganizations.map((org) => {
      return {
        ...org,
        distance:
          org?.coordinates && org?.coordinates.length
            ? Math.min(
              org.coordinates.map((c) =>
                haversineDistance(state.coords, {
                  latitude: c[0],
                  longitude: c[1],
                })
              )
            )
            : 15000,
      }
    })
  }
}

/**
 *
 * @param { {organizations: Array<{id: string, url: string, name: string, level: string, parent: string}>} } state
 * @param getters
 * @returns [key: string]: {id: string, url: string, name: string, level: string, parent: string}
 */
export function organizationLookupByUrl (state, getters) {
  return state.organizations.reduce((map, obj) => {
    map[obj.url] = obj
    return map
  }, {})
}

/**
 *
 * @param { {organizations: Array<{id: string, name: string, level: string, parent: string, settings_inherit: Any, settings_local: Any}>} } state
 * @param getters
 * @returns [key: string]: {id: string, name: string, level: string, parent: string, settings_inherit: Any, settings_local: Any}
 */
export function organizationLookupById (state, getters) {
  return state.organizations.reduce((map, obj) => {
    map[obj.id] = obj
    return map
  }, {})
}

/**
 *
 * @param { {organizations: Array<{id: string, name: string, level: string, parent: string, settings_inherit: Any, settings_local: Any}>} } state
 * @param getters
 * @returns Array<{id: string, name: string, level: string, parent: string, settings_inherit: Any, settings_local: Any}>
 */
export function relevantOrganizationListOrdered (state, getters) {
  if (!state.organizations) {
    return []
  }
  if (state.coords) {
    const result = [...getters.functionRelevantOrganizationsWithDistance]
    result.sort((a, b) => a.name.localeCompare(b.name))
    result.sort((a, b) => a.distance - b.distance)
    return result
  } else {
    const result = [...getters.relevantOrganizations]
    result.sort((a, b) => a.name.localeCompare(b.name))
    return result
  }
}

export function organizationSettingsByUrl (state, getters) {
  if (!state.organizations) {
    return {}
  }
  const orgsByUrl = getters.organizationLookupByUrl
  const parentCache = {}
  const defaultSettings = {
    branding: "_default",
  }
  function fillData (orgUrl) {
    if (Object.prototype.hasOwnProperty.call(parentCache, orgUrl)) {
    } else {
      if (orgsByUrl[orgUrl].parent) {
        fillData(orgsByUrl[orgUrl].parent)
      }
      parentCache[orgUrl] = deepMerge(
        {},
        defaultSettings,
        parentCache[orgsByUrl[orgUrl].parent] ?? {},
        orgsByUrl[orgUrl]?.settings_inherit ?? {}
      )
    }
  }

  state.organizations.forEach((org) => fillData(org.url))
  state.organizations.forEach((org) => {
    parentCache[org.url] = deepMerge(
      {},
      parentCache[org.url],
      orgsByUrl[org.url]?.settings_local ?? {}
    )
  })

  return parentCache
}

export function shortPathTree (state, getters) {
  if (!state.organizations) {
    return {}
  }
  const settingsByUrl = getters.organizationSettingsByUrl
  const orgsByUrl = getters.organizationLookupByUrl

  const pathTemp = {
    null: { parent: null, children: {}, shortName: null },
  }

  function findPathToRoot (orgUrl) {
    if (!orgUrl) {
      return null
    }
    const org = orgsByUrl[orgUrl]
    if (Object.prototype.hasOwnProperty.call(pathTemp, orgUrl)) {
      return orgUrl
    }
    const shortName = settingsByUrl[org.url]?.short_path ?? null
    if (shortName) {
      const parent = findPathToRoot(org.parent)
      pathTemp[orgUrl] = {
        shortName,
        children: {},
        parent: parent,
        url: orgUrl,
      }
      pathTemp[parent].children[shortName] = orgUrl
      return orgUrl
    } else {
      return findPathToRoot(org.parent)
    }
  }

  state.organizations.forEach((org) => {
    findPathToRoot(org.url)
  })

  return pathTemp
}
